import { theme } from '#app/theme/theme.js';
import { themeCssVariables } from '#app/theme/themeCssVariables.js';
import { createGlobalStyle, css, keyframes } from 'styled-components';

const themeColorsAsCssVariables = css`
  ${themeCssVariables.map(([key, value]) => `${key}: ${value};`)}
`;

const width = keyframes`
  0% {
   width: 20%;
  }

  50% {
    width: 30%;
  }

  100% {
    width: 100%;
  }
`;

const opacity = keyframes`
  0% {
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  90% {
    opacity: 0.5;
  }

  100% {
    opacity: 0;
  }
`;

const teardown = keyframes`
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
`;

const targetedReset = css`
  * {
    outline: none;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    scrollbar-color: ${theme.colorsV2.gray30} transparent;
  }
  html {
    height: 100%;
  }
  :root {
    body {
      // This is necessary to avoid layout shift when the radix dropdown is opened
      margin-right: 0px !important;
      // Needed to override Mantine styles, which is the library that is shipped with the BlockNote editor
      background-color: white;
      color: ${theme.colorsV2.black};
    }
  }
  body {
    min-height: 100%;
    text-size-adjust: 100%;
    line-height: 1.15;
  }
  a {
    text-decoration: none;
    color: inherit;
    cursor: pointer;
  }
  button {
    background-color: transparent;
    color: inherit;
    border-width: 0;
    cursor: pointer;
  }
  fieldset {
    border: none;
  }
  input,
  input:focus,
  input::-moz-focus-inner {
    border: none;
  }
  input:disabled,
  textarea:disabled,
  input:disabled::placeholder,
  textarea:disabled::placeholder {
    -webkit-text-fill-color: ${theme.colorsV2.gray40};
    opacity: 1;
  }
  li {
    display: block;
  }
  ul,
  ol,
  dd {
    list-style: none;
  }
  input,
  button,
  textarea {
    font-family: inherit;
  }
  input,
  button,
  textarea,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: inherit;
    font-weight: inherit;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    text-wrap: balance;
  }

  cite {
    font-style: normal;
  }
  ::selection {
    background: ${theme.colorsV2.yellow20};
  }
  ::-moz-selection {
    background: ${theme.colorsV2.yellow20};
  }

  // Overriding the Radix Dropdown Menu Z-Index because its set waaaaay too high - defaults to 9999
  & [data-radix-popper-content-wrapper] {
    z-index: ${theme.zIndices.menu} !important;
  }
`;

const fontSmoothing = css`
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

export const GlobalStyles = createGlobalStyle`
  ${targetedReset}

  :root {
    ${themeColorsAsCssVariables}
  }

  html {
    overflow-y: scroll;
  }

  button, [type="button"], [type="reset"], [type="submit"] {
    appearance: none;
  }

  body {
    font-family: ${theme.fonts.body};
    background-color: ${theme.colorsV2.white};
    color: ${theme.colorsV2.black};
    ${fontSmoothing}
    text-wrap: pretty;
  }

  #page-transition-animation {
    width: 100%;
    position: fixed;
    z-index: 500;
    background-color: ${theme.colorsV2.brandPurple};
    height: 4px;

    transition: width 0.4s ease-in, opacity 0.4s ease-in;
  }

  .page-transition-animation__active {
    transition: width 0.4s ease-in, opacity 0.4s ease-in;

     animation: ${css`
       ${width} 3s ease-in-out, ${opacity} 3s ease-in-out
     `};
  }

  .page-transition-animation__finished {
    width: 100%;
    opacity: 0;
    animation: ${css`
      ${teardown} 1s ease-in-out
    `};

    transition: width 0.4s ease-in, opacity 0.4s ease-in;
  }

  .page-transition-animation__continue {
    animation: inherit;
    opacity: 1;
  }

  .intercom-lightweight-app {
    z-index: ${theme.zIndices.modal.backdrop - 1} !important;
  }
`;
